import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  PlusCircle,
  Search,
  Package,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Wrench
} from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import KeyboardShortcutsButton from "./KeyboardShortcutsButton";
import Logo from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Check if the current path matches a pattern
  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    // For repair detail pages
    if (path === "/repair") {
      return location.pathname.startsWith("/repair/");
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await signOut();
  };

  const navItems = [
    {
      path: "/",
      label: t("common.dashboard"),
      icon: LayoutDashboard,
    },
    {
      path: "/new-repair",
      label: t("common.newRepair"),
      icon: PlusCircle,
    },
    {
      path: "/search",
      label: t("common.search"),
      icon: Search,
    },
    {
      path: "/stock",
      label: t("common.stock"),
      icon: Package,
    },
  ];

  return (
    <aside
      className={cn(
        "bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 h-screen sticky top-0 z-40",
        collapsed ? "w-20" : "w-64"
      )}
    >
      {/* Header / Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
        <Link to="/" className="flex items-center gap-2 overflow-hidden">
          <Logo size={32} className="h-8 w-8 min-w-[32px]" />
          {!collapsed && (
            <span className="font-bold text-xl dark:text-white truncate">
              {t("app.title")}
            </span>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className="hidden md:flex ml-auto"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-2">
        {navItems.map((item) => (
          <Link key={item.path} to={item.path}>
            <Button
              variant={isActive(item.path) ? "secondary" : "ghost"}
              className={cn(
                "w-full justify-start gap-3",
                collapsed ? "px-2 justify-center" : "px-4",
                isActive(item.path) && "bg-gray-100 dark:bg-gray-700"
              )}
              title={collapsed ? item.label : undefined}
            >
              <item.icon className={cn("h-5 w-5", isActive(item.path) ? "text-primary" : "text-gray-500 dark:text-gray-400")} />
              {!collapsed && <span>{item.label}</span>}
            </Button>
          </Link>
        ))}

        {/* Dynamic Repair Detail Link (only if active and not in list) */}
        {isActive("/repair") && (
          <Button
            variant="secondary"
            className={cn(
              "w-full justify-start gap-3 mt-4",
              collapsed ? "px-2 justify-center" : "px-4",
              "bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800"
            )}
            title={collapsed ? t("repair.repairDetails") : undefined}
          >
            <Wrench className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            {!collapsed && <span>{t("repair.repairDetails")}</span>}
          </Button>
        )}
      </nav>

      {/* Footer / Utilities */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
        <div className={cn("flex items-center", collapsed ? "flex-col gap-4" : "gap-2 justify-between")}>
          <div className={cn("flex", collapsed ? "flex-col gap-2" : "gap-1")}>
            <ThemeSwitcher />
            <LanguageSwitcher />
            <KeyboardShortcutsButton />
          </div>
        </div>

        <Button
          variant="ghost"
          onClick={handleLogout}
          className={cn(
            "w-full justify-start gap-3 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10",
            collapsed ? "px-2 justify-center" : "px-4"
          )}
          title={collapsed ? t("auth.logout") : undefined}
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span>{t("auth.logout")}</span>}
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
